import { useState, useCallback, useEffect } from 'react'
import { favoritesAPI } from '../services/api'
import { toast } from '../services/toastService'
import { useTranslation } from 'react-i18next'
import { useAsyncOperation } from './useAsyncOperation'
import { createLogger } from '../utils/logger'
import useOnboardingStepComplete from './useOnboardingStepComplete'

const logger = createLogger('useFavorites')

/**
 * Custom hook for managing user favorites
 * Provides methods to add, remove, check, and fetch favorites
 * 
 * @param {Object} user - Current user object (null if not authenticated)
 * @param {Object} location - Optional location for distance calculation { latitude, longitude }
 * @returns {Object} Favorites state and methods
 */
export function useFavorites(user, location = null) {
  const { t } = useTranslation()
  const [favorites, setFavorites] = useState([])
  const [favoriteIds, setFavoriteIds] = useState(new Set())
  const [favoriteCount, setFavoriteCount] = useState(0)
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false)
  const { loading, execute } = useAsyncOperation()
  const completeOnboardingStep = useOnboardingStepComplete()

  // Fetch user's favorites
  const fetchFavorites = useCallback(async () => {
    if (!user) {
      setFavorites([])
      setFavoriteIds(new Set())
      setFavoriteCount(0)
      return
    }

    try {
      const params = {}
      if (location?.latitude && location?.longitude) {
        params.latitude = location.latitude
        params.longitude = location.longitude
      }

      const response = await execute(
        () => favoritesAPI.getUserFavorites(params),
        { silent: true }
      )
      const favs = response.data.favorites || []
      setFavorites(favs)
      setFavoriteIds(new Set(favs.map(f => f.entityId)))
      setFavoriteCount(favs.length)
    } catch (error) {
      logger.error('Failed to fetch favorites', error)
      setFavorites([])
      setFavoriteIds(new Set())
    }
  }, [user, location, execute])

  // Check if entity is favorited
  const isFavorite = useCallback((entityId) => {
    return favoriteIds.has(entityId)
  }, [favoriteIds])

  // Add entity to favorites
  const addFavorite = useCallback(async (entityId, notes = null) => {
    if (!user) {
      toast.error(t('auth.login.title'))
      return false
    }

    if (isTogglingFavorite) return false // Prevent concurrent requests

    try {
      setIsTogglingFavorite(true)
      const response = await execute(
        () => favoritesAPI.addFavorite(entityId, notes),
        { successMessage: t('favorites.added') }
      )
      
      setFavoriteIds(prev => new Set([...prev, entityId]))
      setFavoriteCount(prev => prev + 1)
      completeOnboardingStep('favorited')
      
      // Refresh full list if needed
      if (favorites.length > 0) {
        fetchFavorites()
      }
      
      // Return channel info for navigation
      return response
    } catch (error) {
      logger.error('Failed to add favorite', error)
      toast.error(t('toast.error.addFavoriteFailed'))
      return false
    } finally {
      setIsTogglingFavorite(false)
    }
  }, [user, t, favorites.length, fetchFavorites, execute, isTogglingFavorite])

  // Remove entity from favorites
  const removeFavorite = useCallback(async (entityId) => {
    if (!user) return false

    if (isTogglingFavorite) return false // Prevent concurrent requests

    try {
      setIsTogglingFavorite(true)
      await execute(
        () => favoritesAPI.removeFavorite(entityId),
        { successMessage: t('favorites.removed') }
      )
      setFavoriteIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(entityId)
        return newSet
      })
      setFavorites(prev => prev.filter(f => f.entityId !== entityId))
      setFavoriteCount(prev => prev - 1)
      return true
    } catch (error) {
      logger.error('Failed to remove favorite', error)
      return false
    } finally {
      setIsTogglingFavorite(false)
    }
  }, [user, t, execute, isTogglingFavorite])

  // Toggle favorite status
  const toggleFavorite = useCallback(async (entityId, notes = null) => {
    if (isFavorite(entityId)) {
      return await removeFavorite(entityId)
    } else {
      return await addFavorite(entityId, notes)
    }
  }, [isFavorite, addFavorite, removeFavorite])

  // Optimistically patch notify_on_spot for a single favorite in local state
  const updateFavoriteNotify = useCallback((entityId, notifyOnSpot) => {
    setFavorites(prev => prev.map(f =>
      (f.entityId || f.entity?.id) === entityId
        ? { ...f, notifyOnSpot, notify_on_spot: notifyOnSpot }
        : f
    ))
  }, [])

  // Fetch favorites on mount and when user changes
  useEffect(() => {
    fetchFavorites()
  }, [fetchFavorites])

  return {
    favorites,
    favoriteIds,
    favoriteCount,
    loading,
    isTogglingFavorite,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    updateFavoriteNotify,
    refetchFavorites: fetchFavorites
  }
}
